import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ToastProvider } from "@radix-ui/react-toast";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { useI18N } from "@sundae/react-hooks";
import { OverlayContainer } from "@sundae/ui-toolkit";
import { WalletObserverProvider } from "@sundaeswap/wallet-lite";
import { createHashHistory, ReactLocation } from "@tanstack/react-location";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { HelmetProvider } from "react-helmet-async";
import { ErrorLayout } from "../../components";
import { Routes } from "../../pages/RouteProvider";
import LoadingState from "../../components/LoadingState";
import { getProposalsSdk } from "../../graphql/client/proposals";
import { getStats2Sdk } from "../../graphql/client/stats2";
import { AppContextProvider } from "../../stores";
import { DEFAULT_CONFIG, getBrand } from "../../utils/config";
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            suspense: true,
        },
    },
});
const options = {
    observerOptions: {
        persistence: true,
        debug: window.__ENV_CONFIG.envName !== "mainnet",
        metadataResolver: async ({ assetIds, normalizeAssetId }) => {
            const { getAssetsByIds } = await getStats2Sdk();
            const { assets } = await getAssetsByIds({
                ids: assetIds,
            });
            const assetMap = new Map();
            assets.forEach((a) => {
                assetMap.set(normalizeAssetId(a.assetId), a);
            });
            return assetMap;
        },
    },
};
const Providers = ({ children }) => {
    const { t } = useI18N();
    return (_jsx(HelmetProvider, { children: _jsx(OverlayContainer, { children: _jsxs(QueryClientProvider, { client: queryClient, children: [_jsx(WalletObserverProvider, { options: options, children: _jsx(TooltipProvider, { delayDuration: 300, children: _jsx(ToastProvider, { label: t("notifications"), children: children }) }) }), _jsx(ReactQueryDevtools, { position: "bottom-left" })] }) }) }));
};
const ClientApp = ({ project, children, brand, componentProps, bgImage, }) => {
    const { data: config } = useQuery([project], async () => {
        const { getConfig } = await getProposalsSdk();
        try {
            const config = await getConfig({
                project,
            });
            return config.clientConfig;
        }
        catch (e) {
            console.log(e);
            return DEFAULT_CONFIG;
        }
    }, {
        suspense: true,
    });
    const appConfig = useMemo(() => ({
        brand,
        ...config,
    }), [config]);
    return (_jsx(AppContextProvider, { config: appConfig, componentProps: componentProps, bgImage: bgImage, children: children }));
};
export const AppProvider = ({ routes, brand = {}, componentProps, bgImage, }) => {
    const history = createHashHistory();
    const location = new ReactLocation({ history });
    return (_jsx(ErrorBoundary, { FallbackComponent: (props) => {
            return (_jsx(Providers, { children: _jsx(ErrorLayout, { ...props }) }));
        }, children: _jsx(Providers, { children: _jsx(Suspense, { fallback: _jsx(LoadingState, {}), children: _jsx(ClientApp, { project: window.__ENV_CONFIG.project, env: window.__ENV_CONFIG.envName, brand: getBrand(brand), componentProps: componentProps ?? {}, bgImage: bgImage ?? "", children: _jsx(Routes, { location: location, routes: routes }) }) }) }) }));
};
export default AppProvider;
//# sourceMappingURL=AppProvider.js.map